/**
 * 14 - Supply Train (10 §2): the locomotive and its 2-4 fuel wagons stand on straight north-south sidings in
 * a wired rail yard, and LEAVE on a clock shown from touchdown (they are removed from the map and the mission
 * is lost); a full alarm halves the time left. The locomotive is `hard`, the wagons soft: a coupled pair of
 * wagons goes up together, but a wagon never takes the locomotive, the pair beyond an uncoupled gap, or
 * anything on the next siding.
 *
 * The departure time is not a number of seconds chosen here: `finish` takes it from the route the validator
 * measures on the finished map (helpers3aRoute.ts).
 *
 * ROUND4 §1: the train really leaves. The locomotive is a parked prop until its clock runs out, and then a
 * `railEngine` takes its place on the siding, steams north up the track and off the map - so "THE TRAIN LEFT"
 * is something the player watches happen, not a line on the HUD. Wreck the engine first and nothing drives.
 */

import { HALF_PI } from '../../sim/math';
import { PROPS } from '../../sim/props';
import { checker, clutter, guard, guardAtDoor, guardsAbout, insideRect, structure } from './helpers';
import { ON_ROAD, dress, lamp, putBlock } from './helpers3a';
import { checkRuleText, checkTargets, cooksOff, timedRules } from './helpers3aAsserts';
import { deadlineSeconds, measureRoute, retime } from './helpers3aRoute';
import { vehicle, vehiclesOf } from './helpers4Vehicles';
import type { ArchetypeSpec } from './types';

export const wagonsFor = (threat: number): number => (threat <= 3 ? 2 : threat <= 6 ? 3 : 4);

const LOCO = 10;
const WAGON = 8;
/** Locomotive to first wagon: 5.5 m from the wagon's heart to the engine - a cook-off spikes `hard` things to 4.95. */
const LOCO_GAP = 1.5;
/** Coupled (5 m to the next hull: it goes up too) and uncoupled (7.5 m: it does not), turn about. */
const COUPLED = 1;
const UNCOUPLED = 3.5;
/** Siding to siding: 7.5 m from a wagon's heart to the hull on the next track. */
const TRACKS = 9;
/** Seconds of work allowed per target when the departure is set. */
const WORK = 30;
/** How far north the track is laid: past the yard, to where the engine leaves the map. */
const RAIL_OUT = 1;
/** The engine pulls out at this, in metres per second; the tag its departure is woken by. */
const TRAIN_SPEED = 9;
const LEAVES = 'targetsLeave';

export const supplyTrain: ArchetypeSpec = {
  id: 'supply-train',
  name: 'SUPPLY TRAIN',
  nativeBiome: 'arctic',
  baseThreat: 5,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [54, 54], d: [34, 40] },
  sizeFor: () => ({ w: 54, d: 40 }),
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const wagons = wagonsFor(g.threat);
    // The sidings take the side of the yard the road does not come in on.
    const gate = g.ctx.plan.roadEnd.x;
    const westward = gate > (g.rect.x0 + g.rect.x1) / 2;
    const first = westward ? g.inner.x0 + 6 : g.inner.x1 - 6;
    const [north, south] = [g.inner.z0 + 0.5, g.inner.z1 - 1.5];
    const ids: number[] = [];
    let engine: { x: number; z: number } | null = null;
    let left = wagons;
    for (let track = 0; track < 3 && (left > 0 || track === 0); track++) {
      const x = first + (westward ? 1 : -1) * track * TRACKS;
      g.map.road([[x, north - 1], [x, south + 1]], 2.6);
      // Buffer stop in the south, the train backed up to it: engine first on the first siding, then wagons.
      const consist: Array<{ kind: 'locomotive' | 'fuelWagon'; x: number; z: number }> = [];
      // The third siding is the one nearest the gate: its buffer stop stands back from where the road comes in.
      let z = track === 2 ? south - 8 : south;
      if (track === 0) {
        consist.push({ kind: 'locomotive', x, z: z - LOCO / 2 });
        z -= LOCO + LOCO_GAP;
      }
      for (let n = 0; left > 0 && z - WAGON >= north; n++) {
        consist.push({ kind: 'fuelWagon', x, z: z - WAGON / 2 });
        z -= WAGON + (n % 2 === 0 ? COUPLED : UNCOUPLED);
        left--;
      }
      // Coupled vehicles stand a metre apart: the siding is asked for as one block (helpers3a.ts `putBlock`).
      const placed = putBlock(g, consist, ON_ROAD);
      const loco = placed.find((p) => p.kind === 'locomotive');
      if (loco) {
        engine = { x: loco.x, z: loco.z };
        // R4: the engine that steams out when the clock runs out. It IS this prop, so the prop goes with it.
        vehicle(g, {
          kind: 'railEngine', speed: TRAIN_SPEED, wakeTag: LEAVES, replacesPropId: loco.id,
          from: { x: loco.x, z: loco.z }, route: [{ x: loco.x, z: loco.z - 4 }, { x: loco.x, z: 1.5 }],
        });
      }
      ids.push(...placed.map((p) => p.id));
    }
    if (left > 0) g.map.problems.push('supply-train: the yard is too short for the train');
    if (!engine) g.map.problems.push('supply-train: the train has no locomotive');
    // R4: a run of track along the engine's own siding, from its buffer stop to where it leaves the map, so
    // that the locomotive under way has rails under it the whole way. Laid AFTER the stock, whose own beds
    // end where the stock does, and which asks the mask for its room.
    if (engine) g.map.add('rail', engine.x, (south + 1 + RAIL_OUT) / 2, { yaw: HALF_PI, length: south + 1 - RAIL_OUT });

    // The yard office and the water tower on the road's side, a lamp over the sidings.
    const side = { ...g.inner, ...(westward ? { x0: first + 2 * TRACKS + 4 } : { x1: first - 2 * TRACKS - 4 }) };
    const office = structure(g, 'hut', { within: side, margin: 1.2 });
    dress(g, 'watchtower', first + (westward ? 1 : -1) * 4.5, north + 3, { margin: 0.8 });
    lamp(g, first + (westward ? 1 : -1) * 4.5, south - 2);
    structure(g, 'alarmPost', { within: side, margin: 1 });
    clutter(g, 'crate', 4);
    if (office) guardAtDoor(g, office);
    guard(g, first + (westward ? 1 : -1) * 3.5, south - 8, 0);
    guard(g, first + (westward ? 1 : -1) * 3.5, north + 9, 0);
    guardsAbout(g, g.rng.int(2));
    return {
      objective: {
        kind: 'destroyTargets', label: 'WRECK THE TRAIN', targetPropIds: ids, minFraction: 1,
        rules: [
          {
            type: 'timedEvent', atSeconds: 420, effect: 'targetsLeave', hastenAtAlert: 3, hastenFactor: 0.5,
            orders: 'THE TRAIN LEAVES ON THE CLOCK - A FULL ALARM HALVES THE TIME LEFT',
            warning: 'THE TRAIN LEAVES IN',
            reason: 'THE TRAIN LEFT',
          },
          { type: 'recall', mode: 'normal' },
        ],
      },
      finish(def) {
        const route = measureRoute(def);
        if (route) retime(def, (r) => r.effect === 'targetsLeave', deadlineSeconds(def, route, WORK * ids.length));
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('supply-train');
    const train = checkTargets(check, def, ['locomotive', 'fuelWagon']);
    const engines = train.filter((t) => t.kind === 'locomotive');
    const wagons = train.filter((t) => t.kind === 'fuelWagon');
    check(engines.length === 1, `needs one locomotive, has ${engines.length}`);
    check(
      wagons.length === wagonsFor(def.threat),
      `needs ${wagonsFor(def.threat)} fuel wagons at threat ${def.threat}`,
    );
    check(
      def.objective.kind === 'destroyTargets' && def.objective.minFraction === 1,
      'the whole train must be wrecked',
    );
    check(PROPS.locomotive.durability === 'hard' && PROPS.fuelWagon.durability === 'soft', 'engine hard, wagons soft');
    const tracks = [...new Set(train.map((t) => t.x))];
    check(tracks.length <= 3, 'the train stands on at most three sidings');
    for (const t of train) {
      check(insideRect(def.goalZone, t.x, t.z), `${t.kind} ${t.id} stands outside the yard`);
      // A straight siding: a north-south track bed under every vehicle.
      const bedded = def.roads.some((r) => r.points.every((p) => p[0] === t.x)
        && Math.min(...r.points.map((p) => p[1])) <= t.z && Math.max(...r.points.map((p) => p[1])) >= t.z);
      check(bedded, `${t.kind} ${t.id} stands on no siding`);
    }
    const engine = engines[0];
    if (engine) {
      check(wagons.some((w) => w.x === engine.x), 'the locomotive has at least one wagon coupled behind it');
      check(wagons.every((w) => !cooksOff(w, engine)), 'a wagon cooking off must not take the locomotive');
    }
    // Pairs at most: no wagon sets off two others.
    check(
      wagons.every((w) => wagons.filter((o) => o.id !== w.id && cooksOff(w, o)).length <= 1),
      'wagons chain in pairs only',
    );

    // R4: the departure is a locomotive that drives north off the map, not props blinking out.
    const driving = vehiclesOf(def.spawns);
    const [steamer] = driving;
    check(driving.length === 1 && steamer?.kind === 'railEngine', 'the train leaves as a locomotive under way');
    check(
      steamer?.dormantUntil === 'timedEvent' && steamer.wakeTag === LEAVES && steamer.replacesPropId !== undefined,
      'the locomotive under way must replace the parked one, and only when its clock runs out',
    );
    const run = steamer?.route?.points.points ?? [];
    const exit = run[run.length - 1];
    check(steamer?.route?.loop === false && exit !== undefined && exit[1] <= 4, 'the train must steam off the north edge');
    check(run.every((n) => Math.abs(n[0] - (engines[0]?.x ?? -99)) < 0.6), 'the train must keep to its own siding');
    // R4: and there are rails under all of it - the moving engine carries no track bed of its own.
    const rails = def.props.filter((p) => p.kind === 'rail');
    const [track] = rails;
    check(rails.length === 1 && track !== undefined, `needs one run of track, has ${rails.length}`);
    if (track) {
      const half = (track.length ?? 0) / 2;
      check(Math.abs(track.x - (engines[0]?.x ?? -99)) < 0.6, 'the track must lie under the engine siding');
      check(track.z - half <= (exit?.[1] ?? 99) && track.z + half >= (engines[0]?.z ?? -99),
        'the track must reach from the buffer stop to where the engine leaves the map');
    }

    const [leave] = timedRules(def, 'targetsLeave');
    const halved = leave !== undefined && leave.hastenAtAlert === 3 && leave.hastenFactor === 0.5;
    check(halved && (leave.from ?? 'touchdown') === 'touchdown',
      'the train leaves on a clock from touchdown, and a full alarm halves the time left');
    if (leave) {
      const share = leave.atSeconds / def.clockSeconds;
      check(share >= 0.5 && share <= 0.8, `the train leaves at ${leave.atSeconds} s of a ${def.clockSeconds} s clock`);
    }
    checkRuleText(check, def);
    return out;
  },
};
